import { JSX, splitProps } from "solid-js";
import styles from "./Skeleton.module.css";

type Variant = "text" | "card" | "row" | "circle" | "rect";

interface SkeletonProps extends JSX.HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  count?: number;
}

function toCssLength(v: string | number | undefined): string | undefined {
  if (v == null) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

export function Skeleton(props: SkeletonProps) {
  const [own, rest] = splitProps(props, ["variant", "width", "height", "radius", "count", "style", "class"]);
  const variant = own.variant ?? "text";

  const baseStyle = (): JSX.CSSProperties => {
    const s: JSX.CSSProperties = {};
    const w = toCssLength(own.width);
    const h = toCssLength(own.height);
    const r = toCssLength(own.radius);
    if (w) s.width = w;
    if (h) s.height = h;
    if (r) s["border-radius"] = r;
    return s;
  };

  if ((own.count ?? 1) > 1) {
    return (
      <div class={styles.stack}>
        {Array.from({ length: own.count! }).map(() => (
          <div
            {...rest}
            class={`${styles.skeleton} ${styles[variant]} ${own.class ?? ""}`}
            style={{ ...baseStyle(), ...(own.style as JSX.CSSProperties | undefined) }}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  return (
    <div
      {...rest}
      class={`${styles.skeleton} ${styles[variant]} ${own.class ?? ""}`}
      style={{ ...baseStyle(), ...(own.style as JSX.CSSProperties | undefined) }}
      aria-hidden="true"
    />
  );
}

export function SkeletonText(props: { lines?: number; width?: string }) {
  const lines = props.lines ?? 3;
  return (
    <div class={styles.stack}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton variant="text" width={i === lines - 1 ? (props.width ?? "60%") : "100%"} />
      ))}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div class={styles.card}>
      <div class={styles.cardRow}>
        <Skeleton variant="circle" width={40} height={40} />
        <div class={styles.cardLines}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </div>
      </div>
      <Skeleton variant="rect" width="100%" height={64} />
    </div>
  );
}

export function SkeletonAuditRow() {
  return (
    <div class={styles.auditRow}>
      <Skeleton variant="text" width={100} />
      <Skeleton variant="text" width={80} />
      <Skeleton variant="text" width="40%" />
    </div>
  );
}

export function SkeletonSlotGrid(props: { count?: number; size?: number }) {
  const count = props.count ?? 12;
  const size = props.size ?? 40;
  return (
    <div class={styles.slotGrid}>
      {Array.from({ length: count }).map(() => (
        <Skeleton variant="rect" width={size} height={size} />
      ))}
    </div>
  );
}
